/**
 * Repeater for structured list fields.
 *
 * Features, specifications, workflow steps and metrics are stored as JSON arrays. Editors get a
 * simple add/remove/reorder interface rather than a JSON textarea, which is the difference between
 * "administrators need no programming knowledge" and a field only a developer will touch.
 */
import { Button, Input, Textarea } from '@/components/ui';

export type RepeaterShape = { name: string; label: string; type: 'text' | 'textarea' }[];

export function RepeaterField({
  label,
  shape,
  value,
  hint,
  onChange,
}: {
  label: string;
  shape: RepeaterShape;
  value: Record<string, string>[];
  hint?: string;
  onChange: (value: Record<string, string>[]) => void;
}) {
  const rows = Array.isArray(value) ? value : [];

  function updateRow(index: number, field: string, fieldValue: string) {
    onChange(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: fieldValue } : row)));
  }

  function addRow() {
    onChange([...rows, Object.fromEntries(shape.map((field) => [field.name, '']))]);
  }

  function removeRow(index: number) {
    onChange(rows.filter((_, rowIndex) => rowIndex !== index));
  }

  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    const current = next[index]!;
    next[index] = next[target]!;
    next[target] = current;
    onChange(next);
  }

  return (
    <fieldset className="rounded-lg border border-line p-4">
      <legend className="px-1 text-sm font-medium text-ink">{label}</legend>
      {hint && <p className="mb-3 text-xs text-muted">{hint}</p>}

      {rows.length === 0 ? (
        <p className="text-sm text-muted">No entries yet.</p>
      ) : (
        <ol className="space-y-4">
          {rows.map((row, index) => (
            <li key={index} className="rounded-md border border-line bg-raised p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-mono text-xs text-muted">#{index + 1}</span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    aria-label={`Move entry ${index + 1} up`}
                    className="rounded p-1.5 text-muted hover:bg-surface disabled:opacity-30"
                  >
                    <span aria-hidden="true">↑</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => move(index, 1)}
                    disabled={index === rows.length - 1}
                    aria-label={`Move entry ${index + 1} down`}
                    className="rounded p-1.5 text-muted hover:bg-surface disabled:opacity-30"
                  >
                    <span aria-hidden="true">↓</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    aria-label={`Remove entry ${index + 1}`}
                    className="rounded p-1.5 text-danger hover:bg-danger-soft"
                  >
                    <span aria-hidden="true">✕</span>
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {shape.map((field) => {
                  const id = `${label}-${index}-${field.name}`;
                  return (
                    <div key={field.name}>
                      <label htmlFor={id} className="mb-1 block text-xs font-medium text-muted">
                        {field.label}
                      </label>
                      {field.type === 'textarea' ? (
                        <Textarea
                          id={id}
                          rows={2}
                          value={row[field.name] ?? ''}
                          onChange={(event) => updateRow(index, field.name, event.target.value)}
                        />
                      ) : (
                        <Input
                          id={id}
                          value={row[field.name] ?? ''}
                          onChange={(event) => updateRow(index, field.name, event.target.value)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </li>
          ))}
        </ol>
      )}

      <Button variant="secondary" size="sm" onClick={addRow} className="mt-4">
        Add entry
      </Button>
    </fieldset>
  );
}
